import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { css } from '@emotion/react';
import {
  Node as SchemaNode,
  PhoneHoursScheduleRule,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { useQueryClient } from 'react-query';
import { CallRouteV1 } from '@frontend/api-call-route';
import { GlobalBannerView } from '@frontend/global-info-banner';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate, useSettingsMatch, useInterRouter } from '@frontend/settings-routing';
import { SettingsPageLayout } from '@frontend/settings-ui';
import { theme } from '@frontend/theme';
import { ContentLoader, useAlert } from '@frontend/design-system';
import { findMediaItem } from '../../components/audio-picker/audio-picker';
import { PhoneHoursNodeState } from '../../components/call-routes/call-route-details/add-step-panel/types';
import { CallRouteActionBar } from '../../components/call-routes/call-route-details/call-route-action-bar';
import { CallRouteDetailsHeader } from '../../components/call-routes/call-route-details/call-route-details-header';
import { CallRouteFlow } from '../../components/call-routes/call-route-details/call-route-flow/call-route-flow';
import { CallRouteModals } from '../../components/call-routes/call-route-details/call-route-modals';
import { CallRouteSidebar } from '../../components/call-routes/call-route-details/call-route-sidebar';
import { SidePanel, SidePanelType } from '../../components/call-routes/call-route-details/constants';
import { useCallRouteData } from '../../components/call-routes/call-route-details/hooks/use-call-route-data';
import { useCallRouteGraphState } from '../../components/call-routes/call-route-details/hooks/use-call-route-graph-state';
import { useCallRouteModals } from '../../components/call-routes/call-route-details/hooks/use-call-route-modals';
import { useNodeActions } from '../../components/call-routes/call-route-details/hooks/use-node-actions';
import {
  getCallRouteStepOptions,
  getDeviceExtensionRouteStepOptions,
} from '../../components/call-routes/helpers/add-step-helpers';
import { getRelevantLocationIds } from '../../components/call-routes/helpers/ai-receptionist-credit-utils';
import { sortRouteExtensionNumbers, sortRoutePhoneNumbers } from '../../components/call-routes/helpers/dialables';
import { processNodesForSave } from '../../components/call-routes/helpers/graph/data-processing';
import {
  getPhoneTreeOverrideUsage,
  isAssociativeNode,
  isPhoneHoursAncestor,
  isPhoneHoursDescendant,
  isWeaveAssistantAncestor,
  isWeaveAssistantDescendant,
} from '../../components/call-routes/helpers/graph/node-utils';
import { shouldShowWarning } from '../../components/call-routes/helpers/graph/warning-validators';
import { buildIncomingDialables } from '../../components/call-routes/helpers/incoming-dialables';
import { logError } from '../../components/call-routes/helpers/utils';
import { NodeAction } from '../../components/call-routes/types';
import { useCallObjectsData } from '../../components/call-routes/use-call-objects-data';
import { CachedAudioScrubber } from '../../components/common/cached-audio-scrubber';
import { DayHoursList } from '../../components/phone-hours/day-hours-list';
import { invalidatePhoneTreeQueries, queryKeys } from '../../query-keys';
import { useHoursManagementPermission, useOverridesManagementPermission } from '../../userManagementPermission';
import { buildAudioLibraryPath } from '../../utils/media-path';
import { useTenantLocationIds } from '../../utils/use-tenant-location-ids';

export const CallRouteDetails = () => {
  const { t } = useTranslation('phone');
  const {
    params: { id: callRouteId },
    // crid, crn and nid are set in the search params when the user is redirected from the call route details page.
    // (from clicking on a call route link in a node in the call route flow)
    search: { crid: redirectCallRouteId, crn: redirectCallRouteName, nid: redirectNodeId },
  } = useSettingsMatch<{ Params: { id: string }; Search: { crid?: string; crn?: string; nid?: string } }>();
  const alert = useAlert();
  const queryClient = useQueryClient();

  const { navigate: settingsNavigate, context } = useSettingsNavigate<{
    contextNodeId?: string;
    contextPrimitiveId?: string;
  }>();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );
  const tenantLocationIds = useTenantLocationIds();

  const { getScopeName, isSingleTypeScope } = useAppScopeStore();

  // Manage all modals/trays
  const modals = useCallRouteModals();

  // Fetch call route related data
  const {
    callRoute,
    callRouteIsLoading,
    isDeviceExtensionRoute,
    phoneNumbersIsLoading,
    unallocatedPhoneNumbers,
    unallocatedPhoneNumbersIsLoading,
    phoneOverridesIsLoading,
    overridesData,
    extendedCallRoute,
    isLoading,
  } = useCallRouteData({
    callRouteId,
    tenantId: settingsTenantLocation?.phoneTenantId ?? '',
    tenantLocationIds,
    onCallRouteError: () => settingsNavigate({ to: '/phone/call-routes' }),
  });

  // Fetch call objects data
  const callObjectsData = useCallObjectsData(settingsTenantLocation);

  // Manage call route graph state
  const {
    callRouteNodes,
    callRouteEdges,
    extendedNodes,
    tempEditingNodes,
    tempEditingEdges,
    contextNodeId,
    getNodes,
    getEdges,
    resetGraphState,
    updateEditingState,
    setContextNodeId,
  } = useCallRouteGraphState({
    callRouteId,
    callRoute,
    tenantId: settingsTenantLocation?.phoneTenantId ?? '',
    context,
    onShouldForceReset: modals.forceReset.openModal,
    isCallObjectsDataLoading: callObjectsData.someIsLoading,
  });

  // Manage node actions (add, edit, delete, duplicate)
  const {
    processingNodeId: _processingNodeId, // Used internally by the hook
    setProcessingNodeId,
    processingActionType,
    setProcessingActionType,
    tempProcessingData,
    processingNode, // The node that the user is currently acting upon (adding, editing, deleting).
    tempDuplicateData,
    handleAddStep: _handleAddStep, // Used internally by the hook
    handleEditStep: _handleEditStep, // Used internally by the hook
    handleDeleteStep,
    handleDuplicateSubGraph,
    handleEditBreakHourNode,
    processStepAction,
    clearProcessingState,
    getEditingNodeInitialData,
    getAncestorPhoneTrees,
    handleConfirmEditStep,
    handleConfirmAddStep,
    handleConfirmDeleteStep,
    handleConfirmDuplicateStep,
  } = useNodeActions({
    callRouteId,
    getNodes,
    getEdges,
    updateEditingState,
    setContextNodeId,
    callRouteEdges,
    tempEditingEdges,
    extendedNodes,
    openConfirmationModal: modals.confirmation.openModal,
    closeAddStepModal: modals.addStep.closeModal,
    closeEditBreakModal: modals.editBreak.closeModal,
    overrides: overridesData,
    openPhoneTreeOverrideUsageModal: modals.phoneTreeOverrideUsage.openModal,
  });

  const [activePanel, setActivePanel] = useState<SidePanelType>();
  const [triggerPhoneHoursEdit, setTriggerPhoneHoursEdit] = useState(false);

  const overridesManagementPermission = useOverridesManagementPermission();
  const hasOverridePermission = overridesManagementPermission(extendedCallRoute?.locationId ?? '');
  const hoursManagementPermission = useHoursManagementPermission();
  const hasHoursPermission = hoursManagementPermission(extendedCallRoute?.locationId ?? '');

  const callRouteName = callRoute?.callRoute.name ?? 'Unknown Call Route Name';

  // Close out any open modals when the call route id changes.
  useEffect(() => {
    modals.deleteModal.closeModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callRouteId]);

  useEffect(() => {
    if (modals.sidePanel.modalProps.show === false) {
      setActivePanel(undefined);
    }
  }, [modals.sidePanel.modalProps.show]);

  useEffect(() => {
    if (modals.addStep.modalProps.show === false && modals.confirmation.modalProps.show === false) {
      // Clear out any processingState (e.g. processingNodeId) whenever the add step or confirmation modals are all
      // closed since this means the user has now added, edited or deleted a node or closed the modal without adding
      // or deleting a node.
      clearProcessingState();
    }
  }, [modals.addStep.modalProps.show, modals.confirmation.modalProps.show, clearProcessingState]);

  // Get all of the office hours nodes in the current call route (including temp edits)
  const getOfficeHoursNodes = useCallback(() => {
    const nodes = getNodes();
    const phoneHourCallObjects = nodes.filter((node) => node.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS);
    return phoneHourCallObjects;
  }, [getNodes]);

  // Mutation for saving call route
  const { mutate: saveCallRoute, isLoading: saveCallRouteIsLoading } = CallRouteV1.Mutations.useSaveMutation({
    options: {
      onSuccess: () => {
        handleReset();
        queryClient.invalidateQueries([settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.listPhoneHours()]);
        // Invalidate all queries whose query key contains "voicemail-box-greetings"
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey.some((key) => typeof key === 'string' && key.includes('voicemail-box-greetings')),
        });
        invalidatePhoneTreeQueries(queryClient);
        queryClient.invalidateQueries({
          predicate: (query) => Array.isArray(query.queryKey) && query.queryKey.some((key) => key === 'Usage'),
        });
        alert.success(t('Call Route saved successfully.'));
      },
      onError: (error) => {
        const errorMessage = t('Failed to save Call Route: PC-a9b0c1d');
        alert.error(errorMessage);
        logError(errorMessage, 'CallRouteDetails:saveCallRoute', {
          error,
          callRouteId,
        });
      },
    },
  });

  // The step options to allow selecting when adding a new step (or editing an existing step) in the side panel.
  // This depends on whether the call route is a device extension route or a regular call route, and also on
  // whether the current processing node is a descendant of certain node types.
  const memoizedStepOptions = useMemo(() => {
    if (isDeviceExtensionRoute) {
      return getDeviceExtensionRouteStepOptions();
    }
    return getCallRouteStepOptions({
      isPhoneTreeDescendant: processingNode?.isPhoneTreeDescendent ?? false,
      isPhoneHoursDescendant: processingNode
        ? isPhoneHoursDescendant(processingNode.id, getNodes(), getEdges())
        : false,
      isPhoneHoursAncestor: processingNode ? isPhoneHoursAncestor(processingNode.id, getNodes(), getEdges()) : false,
      isWeaveAssistantDescendant: processingNode
        ? isWeaveAssistantDescendant(processingNode.id, getNodes(), getEdges())
        : false,
      isWeaveAssistantAncestor: processingNode
        ? isWeaveAssistantAncestor(processingNode.id, getNodes(), getEdges())
        : false,
    });
  }, [isDeviceExtensionRoute, processingNode, getNodes, getEdges]);

  // Callbacks for call route flow
  // TODO: refactor all of these into a separate hook
  /**
   * Callback function for when a call object link is clicked in a node.
   * Redirects the user to the appropriate page based on the type of node clicked.
   */
  const handleCallObjectLinkClickedImpl = useCallback(
    ({ callObjectId, nodeId, type }: { callObjectId: string; nodeId: string; type: CallRouteV1.Types.NodeType }) => {
      const routeMap: Partial<Record<CallRouteV1.Types.NodeType, string>> = {
        [CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP]: '/phone/call-groups/:id',
        [CallRouteV1.Types.NodeType.NODE_TYPE_CALL_ROUTE]: '/phone/call-routes/:id',
        [CallRouteV1.Types.NodeType.NODE_TYPE_DEVICE_EXTENSION]: '/phone/call-routes/:id',
        [CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE]: '/phone/call-queues/:id',
        [CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE]: '/phone/phone-tree-quick-actions/:id',
        [CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX]: '/phone/voicemail-box/:id',
        [CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE]: '/phone/devices/:id',
      };

      const route = routeMap[type];

      if (!route) {
        const errorMessage = `Unhandled node type: ${type}: PC-a1b2c3d`;
        logError(errorMessage, 'CallRouteDetails:handleCallObjectLinkClicked', {
          callObjectId,
          nodeId,
          type,
          callRouteId,
        });
        alert.error(errorMessage);
        return;
      }

      const baseSearch = {
        crid: extendedCallRoute?.callRouteId,
        crn: extendedCallRoute?.name,
        nid: nodeId,
      };

      const search =
        type === CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE ? { ...baseSearch, id: callObjectId } : baseSearch;

      settingsNavigate({
        to: route as any,
        params: { id: callObjectId },
        search,
      });
    },
    [extendedCallRoute?.callRouteId, extendedCallRoute?.name, settingsNavigate, alert, callRouteId]
  );

  const handleCallObjectLinkClickedRef = useRef(handleCallObjectLinkClickedImpl);
  handleCallObjectLinkClickedRef.current = handleCallObjectLinkClickedImpl;
  // Stable callback that never changes identity — safe to pass into memoized node types
  const handleCallObjectLinkClicked = useCallback(
    (args: { callObjectId: string; nodeId: string; type: CallRouteV1.Types.NodeType }) =>
      handleCallObjectLinkClickedRef.current(args),
    []
  );

  /**
   * This is a custom audio player component that is used in the call route flow for the PlayMessage and VoicemailBox nodes.
   * In the case of the voicemailBox node, the downloadUrl is passed in as a prop. In the case of the PlayMessage node, the
   * mediaItemId is passed in as a prop and so we need to build the download the URL for the audio file in those cases.
   */
  const AudioPlayerComponentImpl = useCallback(
    (props: { mediaItemId: string; downloadUrl?: string }) => {
      if (!props.mediaItemId && !props.downloadUrl) {
        const errorMessage = 'Missing mediaItemId and downloadUrl: PC-e4f5g6h';
        alert.error(errorMessage);
        logError(errorMessage, 'CallRouteDetails:AudioPlayerComponent', {
          mediaItemId: props.mediaItemId,
          downloadUrl: props.downloadUrl,
          callRouteId,
        });
        return null;
      }

      let filePath = '';
      if (props.downloadUrl) {
        filePath = props.downloadUrl;
      } else {
        const media = findMediaItem(props.mediaItemId, callObjectsData.audioFiles?.allMedia || []);
        if (media) {
          filePath = buildAudioLibraryPath({ media });
        } else {
          const errorMessage = 'Could not find media item for audio player: PC-h7i8j9k';
          alert.error(errorMessage);
          logError(errorMessage, 'CallRouteDetails:AudioPlayerComponent', {
            mediaItemId: props.mediaItemId,
            callRouteId,
          });
          return null;
        }
      }

      return <CachedAudioScrubber singlePlayer customWidth={205} filePath={filePath} mediaId={props.mediaItemId} />;
    },
    [callObjectsData.audioFiles, callRouteId, alert]
  );

  const AudioPlayerComponentRef = useRef(AudioPlayerComponentImpl);
  AudioPlayerComponentRef.current = AudioPlayerComponentImpl;
  // Stable callback that never changes identity — safe to pass into memoized node types
  const AudioPlayerComponent = useCallback(
    (props: { mediaItemId: string; downloadUrl?: string }) => AudioPlayerComponentRef.current(props),
    []
  );

  /**
   * Custom component for rendering the DayHoursList component in the call route flow.
   */
  const DayHoursListComponentImpl = useCallback(
    (props: { phoneHours: PhoneHoursScheduleRule[] }) => <DayHoursList phoneHours={props.phoneHours} />,
    []
  );

  const DayHoursListComponentRef = useRef(DayHoursListComponentImpl);
  DayHoursListComponentRef.current = DayHoursListComponentImpl;
  // Stable callback that never changes identity — safe to pass into memoized node types
  const DayHoursListComponent = useCallback(
    (props: { phoneHours: PhoneHoursScheduleRule[] }) => DayHoursListComponentRef.current(props),
    []
  );

  /**
   * Callback function for when the plus icon is clicked in a node in the canvas.
   */
  const handlePlusIconClickedImpl = useCallback(
    ({ parentNodeId }: { parentNodeId: string }) => {
      setProcessingNodeId(parentNodeId);
      setProcessingActionType(NodeAction.Add);
      modals.addStep.openModal();
    },
    [modals.addStep, setProcessingNodeId, setProcessingActionType]
  );

  const handlePlusIconClickedRef = useRef(handlePlusIconClickedImpl);
  handlePlusIconClickedRef.current = handlePlusIconClickedImpl;
  // Stable callback that never changes identity — safe to pass into memoized node types
  const handlePlusIconClicked = useCallback(
    (args: { parentNodeId: string }) => handlePlusIconClickedRef.current(args),
    []
  );

  /**
   * Callback function for when the edit icon is clicked in a node in the canvas.
   */
  const handleEditIconClickedImpl = useCallback(
    ({ nodeId }: { nodeId: string }) => {
      setProcessingNodeId(nodeId);
      setProcessingActionType(NodeAction.Edit);
      const nodesToUse = getNodes();
      const nodeToEdit = nodesToUse.find((node) => node.id === nodeId);
      if (!nodeToEdit) {
        const errorMessage = 'Could not find node to edit: PC-i7j8k9l';
        alert.error(errorMessage);
        logError(errorMessage, 'CallRouteDetails:handleEditIconClicked', {
          nodeId,
          availableNodes: nodesToUse.map((n) => n.id),
          callRouteId,
        });
        return;
      }
      if (nodeToEdit.type === CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS) {
        modals.editBreak.openModal();
      } else {
        modals.addStep.openModal();
      }
    },
    [
      modals.addStep,
      modals.editBreak,
      getNodes,
      alert,
      callRouteId,
      setProcessingNodeId,
      setProcessingActionType,
      _handleEditStep,
    ]
  );

  const handleEditIconClickedRef = useRef(handleEditIconClickedImpl);
  handleEditIconClickedRef.current = handleEditIconClickedImpl;
  // Stable callback that never changes identity — safe to pass into memoized node types
  const handleEditIconClicked = useCallback((args: { nodeId: string }) => handleEditIconClickedRef.current(args), []);

  const handleSavePhoneHours = useCallback(
    ({ nodeId, nodeUpdate }: { nodeId: string; nodeUpdate: PhoneHoursNodeState }) => {
      setProcessingNodeId(nodeId);
      setProcessingActionType(NodeAction.Edit);
      _handleEditStep(
        {
          nodeState: nodeUpdate,
          additionalData: {},
        },
        nodeId
      );
    },
    [_handleEditStep, setProcessingActionType, setProcessingNodeId]
  );

  /**
   * Callback function for when the delete icon is clicked in a node in the canvas.
   */
  const handleDeleteIconClickedImpl = useCallback(
    ({ nodeId }: { nodeId: string }) => {
      const nodesToUse = getNodes();
      const edgesToUse = getEdges();
      const nodeToDelete = nodesToUse.find((node) => node.id === nodeId);
      if (!nodeToDelete) {
        const errorMessage = 'Could not find node to delete: PC-m0n1o2p';
        alert.error(errorMessage);
        logError(errorMessage, 'CallRouteDetails:handleDeleteIconClicked', {
          nodeId,
          availableNodes: nodesToUse.map((n) => n.id),
          callRouteId,
        });
        return;
      }

      // Check if this node or any of its descendants are used in any phone tree overrides. If so, if the node to be
      // deleted is associative (which would cause all of its descendants to be deleted), we need to show the user a
      // warning modal listing all of the overrides.
      const phoneTreeNodeOverrides = getPhoneTreeOverrideUsage({
        nodeId,
        nodes: nodesToUse,
        edges: edgesToUse,
        overrides: overridesData,
      });
      const hasPhoneTreeOverrides = Object.keys(phoneTreeNodeOverrides).length > 0;
      const isAssociative = isAssociativeNode(nodeToDelete.type);

      if (hasPhoneTreeOverrides && isAssociative) {
        setProcessingActionType(NodeAction.Delete);
        setProcessingNodeId(nodeId);
        modals.phoneTreeOverrideUsage.openModal();
        return;
      }

      // Check if we need to show a warning modal before deleting the node.
      const showWarning = shouldShowWarning({
        nodeId,
        actionType: NodeAction.Delete,
        nodes: nodesToUse,
        edges: edgesToUse,
      });
      if (showWarning) {
        setProcessingActionType(NodeAction.Delete);
        setProcessingNodeId(nodeId);
        modals.confirmation.openModal();
      } else {
        handleDeleteStep(nodeId);
      }
    },
    [
      modals.confirmation,
      getNodes,
      getEdges,
      alert,
      callRouteId,
      handleDeleteStep,
      setProcessingNodeId,
      setProcessingActionType,
      modals.phoneTreeOverrideUsage,
      overridesData,
    ]
  );

  // We use a ref to hold the latest implementation so that the stable callback reference
  // passed to CallRouteFlow never changes, avoiding React Flow's nodeTypes recreation warning.
  const handleDeleteIconClickedRef = useRef(handleDeleteIconClickedImpl);
  handleDeleteIconClickedRef.current = handleDeleteIconClickedImpl;
  // Stable callback that never changes identity — safe to pass into memoized node types
  const handleDeleteIconClicked = useCallback(
    (args: { nodeId: string }) => handleDeleteIconClickedRef.current(args),
    []
  );

  const handleSaveChanges = () => {
    if (!tempEditingNodes || !tempEditingEdges || !callRoute) {
      const errorMessage = 'Missing data to save: PC-o0p1q2r';
      alert.error(errorMessage);
      logError(errorMessage, 'CallRouteDetails:handleSaveChanges', {
        tempEditingNodes,
        tempEditingEdges,
        callRouteId,
        callRoute,
      });
      return;
    }

    const updatedNodes = processNodesForSave(tempEditingNodes);

    saveCallRoute({
      callRouteId: callRoute.callRouteId,
      edges: tempEditingEdges,
      // Type assertion here is to tell typescript that the nodes are of type CallRouteV1.Types.Node[]
      // which is really the same type expected by the API.
      nodes: updatedNodes as SchemaNode[],
      rootInstructionSetId: callRoute.callRoute.rootInstructionSetId,
    });
  };

  /**
   * Async version of handleSaveChanges that returns a Promise.
   * Used by the Call Route Canvas to save changes but also used by the override tray to allow
   * saving changes to the call route from within there.
   */
  const handleSaveChangesAsync = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!tempEditingNodes || !tempEditingEdges || !callRoute) {
        const errorMessage = 'Missing data to save: PC-o0p1q2r';
        alert.error(errorMessage);
        logError(errorMessage, 'CallRouteDetails:handleSaveChangesAsync', {
          tempEditingNodes,
          tempEditingEdges,
          callRouteId,
          callRoute,
        });
        reject(new Error(errorMessage));
        return;
      }

      const updatedNodes = processNodesForSave(tempEditingNodes);

      saveCallRoute(
        {
          callRouteId: callRoute.callRouteId,
          edges: tempEditingEdges,
          nodes: updatedNodes as SchemaNode[],
          rootInstructionSetId: callRoute.callRoute.rootInstructionSetId,
        },
        {
          onSuccess: () => resolve(),
          onError: (error) => reject(error),
        }
      );
    });
  }, [tempEditingNodes, tempEditingEdges, callRoute, saveCallRoute, alert, callRouteId]);

  const handleReset = () => {
    resetGraphState();
    clearProcessingState();
    modals.addStep.closeModal();
  };

  const handleUndoChanges = () => {
    handleReset();
    const rootNode = callRouteNodes?.find((node) => node.type === CallRouteV1.Types.NodeType.NODE_TYPE_START);
    if (rootNode) {
      setContextNodeId(rootNode.id);
    }
  };

  const handlePanelIconButtonClick = (panel: SidePanelType, shouldTriggerEdit = false) => {
    if (activePanel === panel) {
      // If the user clicks on the same panel icon button that is already open, then we need to close the panel.
      setActivePanel(undefined);
      setTriggerPhoneHoursEdit(false);
      modals.sidePanel.closeModal();
      return;
    }
    modals.addStep.closeModal();

    // Set edit mode BEFORE setting active panel and opening modal to prevent flash
    if (panel === SidePanel.PhoneHours && shouldTriggerEdit) {
      setTriggerPhoneHoursEdit(true);
    } else {
      setTriggerPhoneHoursEdit(false);
    }

    setActivePanel(panel);
    modals.sidePanel.openModal();
  };

  const handleIncomingDialablesMoreClick = useCallback(
    () => handlePanelIconButtonClick(SidePanel.Numbers),
    [handlePanelIconButtonClick]
  );

  const handlePanelClose = () => {
    setActivePanel(undefined);
    setTriggerPhoneHoursEdit(false); // Reset edit trigger when closing
    modals.sidePanel.closeModal();
  };

  // Header content for the page header (section with breadcrumbs, heading and action buttons)
  const headerContent = CallRouteDetailsHeader({
    callRouteName,
    isLoading: callRouteIsLoading,
    isDeviceExtensionRoute,
    redirectCallRouteId,
    redirectCallRouteName,
    redirectNodeId,
    onRenameClick: modals.renameCallRoute.openModal,
    onDeleteClick: modals.deleteModal.openModal,
  });

  const { settingsRouterPath } = useInterRouter(['settingsRouterPath']);
  const callObjectId = (settingsRouterPath.search as Record<string, string>)?.callObjectId ?? '';
  const nodeId = callRouteNodes?.find(
    (n) =>
      ('playMessageExpansion' in n.callObject && n.callObject.playMessageExpansion?.mediaItemId === callObjectId) ||
      (!!callObjectId && n.callObject.primitiveId === callObjectId)
  )?.id;

  const incomingDialables = useMemo(() => {
    const sortedPhoneNumbers = sortRoutePhoneNumbers(extendedCallRoute?.phoneNumbers ?? []);
    const sortedExtensionNumbers = sortRouteExtensionNumbers(extendedCallRoute?.extensionNumbers ?? []);

    return buildIncomingDialables({
      isDeviceExtensionRoute,
      phoneNumbers: sortedPhoneNumbers.map((phoneNumber) => ({
        label: formatPhoneNumber(phoneNumber.phoneNumber),
        name: phoneNumber.displayName || undefined,
      })),
      extensionNumbers: sortedExtensionNumbers,
    });
  }, [extendedCallRoute?.extensionNumbers, extendedCallRoute?.phoneNumbers, isDeviceExtensionRoute]);

  // Compute unique locationIds from phone numbers assigned to this call route for voice AI receptionist credit checks.
  // If the call route has no phone numbers assigned, fall back to all phone numbers' locationIds.
  const callRoutePhoneNumberLocationIds = useMemo(
    () =>
      getRelevantLocationIds({
        callRoutePhoneNumbers: extendedCallRoute?.phoneNumbers ?? [],
        allPhoneNumbers: callObjectsData.allPhoneNumbers ?? [],
      }),
    [extendedCallRoute?.phoneNumbers, callObjectsData.allPhoneNumbers]
  );

  return (
    <SettingsPageLayout css={{ scrollbarGutter: 'stable' }}>
      <Page
        bgColor={theme.colors.white}
        css={css`
          max-width: 100%;
          padding: 0;
          display: flex;
          flex-direction: column;

          .page-header {
            padding: ${theme.spacing(2, 5)};
            margin-bottom: 0;
            border-bottom: 1px solid ${theme.colors.neutral10};
            height: 96px;
          }

          nav {
            margin-bottom: 0;
          }
        `}
      >
        {extendedCallRoute?.callRoutePhoneOverride?.enabled && (
          <GlobalBannerView
            title={t('Active Override')}
            type='warning'
            message={
              isDeviceExtensionRoute
                ? t('There is currently an active override rerouting the "{{routeName}}" Device Route. ', {
                    routeName: callRouteName,
                  })
                : t(
                    'This Call Route has an active override and will not direct calls normally until the override is removed.'
                  )
            }
            isDismissible={false}
            action={{
              label: hasOverridePermission ? t('Edit Override') : t('View Override'),
              onClick: modals.editOverride.openModal,
            }}
          />
        )}

        <Page.Header>
          <Page.Header.SettingsBreadcrumbs breadcrumbs={headerContent.breadcrumbs} />
          <Page.Header.Heading>
            {headerContent.heading}
            <Page.Header.Action>{headerContent.action}</Page.Header.Action>
          </Page.Header.Heading>
        </Page.Header>

        <Page.Body
          css={css`
            display: flex;
            flex: 1;
            flex-direction: column;
          `}
          id='call-route-page-body-container'
        >
          <div
            css={css`
              display: flex;
              flex: 1;
            `}
            id='call-route-body-section'
          >
            {/* Main Content */}
            <section
              id='call-route-flow-section'
              css={css`
                flex: 1;
                background-color: ${theme.colors.neutral5};
                position: relative;
              `}
            >
              {/*
                NOTE: added extendedCallRoute?.callRouteId to the condition here even though it seems like it is not
                required by CallRouteFlow component in order to prevent rerender and causing the Flow component from
                recreating it's node types since extendedCallRoute?.callRouteId is actually used in the dependency
                array of one of the callback functions passed to CallRouteFlow.
              */}
              {!!callRouteNodes && !!callRouteEdges && !!extendedCallRoute?.callRouteId && (
                <CallRouteFlow
                  nodes={extendedNodes}
                  edges={tempEditingEdges ?? callRouteEdges}
                  canEdit={true}
                  hoursManagementPermission={hasHoursPermission}
                  isDeviceExtensionRoute={isDeviceExtensionRoute}
                  locationCreditDetails={callObjectsData.locationCreditDetails}
                  callRoutePhoneNumberLocationIds={callRoutePhoneNumberLocationIds}
                  onCallObjectLinkClick={handleCallObjectLinkClicked}
                  onPlusIconClick={handlePlusIconClicked}
                  onEditIconClick={handleEditIconClicked}
                  onDeleteIconClick={handleDeleteIconClicked}
                  onIncomingDialablesMoreClick={handleIncomingDialablesMoreClick}
                  AudioPlayerComponent={AudioPlayerComponent}
                  DayHoursListComponent={DayHoursListComponent}
                  initialNodeId={contextNodeId || nodeId}
                  incomingDialables={incomingDialables}
                />
              )}
              <ContentLoader show={isLoading} />
            </section>

            {/* Sidebar - contains the Numbers, Hours, Overrides and Location (optional) side buttons */}
            <CallRouteSidebar
              activePanel={activePanel}
              isDeviceExtensionRoute={isDeviceExtensionRoute}
              isSingleTypeScope={isSingleTypeScope}
              callRouteIsLoading={callRouteIsLoading}
              phoneNumbersIsLoading={phoneNumbersIsLoading}
              unallocatedPhoneNumbersIsLoading={unallocatedPhoneNumbersIsLoading}
              phoneOverridesIsLoading={phoneOverridesIsLoading}
              onPanelClick={handlePanelIconButtonClick}
            />
          </div>

          {/* Bottom Action Bar to save/undo changes */}
          <CallRouteActionBar
            hasChanges={!!(tempEditingNodes || tempEditingEdges)}
            isSaving={saveCallRouteIsLoading}
            isDeviceExtensionRoute={isDeviceExtensionRoute}
            onSave={handleSaveChanges}
            onUndo={handleUndoChanges}
          />
        </Page.Body>
      </Page>

      {/* All Modals and Trays */}
      <CallRouteModals
        modals={modals}
        extendedCallRoute={extendedCallRoute}
        callRoute={callRoute}
        callRouteName={callRouteName}
        callRouteId={callRouteId}
        isDeviceExtensionRoute={isDeviceExtensionRoute}
        unallocatedPhoneNumbers={unallocatedPhoneNumbers ?? []}
        settingsTenantLocation={settingsTenantLocation}
        callObjectsData={callObjectsData}
        processStepAction={processStepAction}
        handleDuplicateSubGraph={handleDuplicateSubGraph}
        getEditingNodeInitialData={getEditingNodeInitialData}
        getAncestorPhoneTrees={getAncestorPhoneTrees}
        getNodes={getNodes}
        getEdges={getEdges}
        stepOptions={memoizedStepOptions}
        processingNode={processingNode}
        processingActionType={processingActionType}
        tempProcessingData={tempProcessingData}
        tempDuplicateData={tempDuplicateData}
        handleEditBreakHourNode={handleEditBreakHourNode}
        handleConfirmEditStep={handleConfirmEditStep}
        handleConfirmAddStep={handleConfirmAddStep}
        handleConfirmDeleteStep={handleConfirmDeleteStep}
        handleConfirmDuplicateStep={handleConfirmDuplicateStep}
        activePanel={activePanel}
        onPanelClose={handlePanelClose}
        onOpenPhoneHoursPanel={() => handlePanelIconButtonClick(SidePanel.PhoneHours, true)}
        isSingleTypeScope={isSingleTypeScope}
        locationName={getScopeName(extendedCallRoute?.locationId ?? '')}
        officeHourNodes={getOfficeHoursNodes()}
        onSavePhoneHours={handleSavePhoneHours}
        hoursManagementPermission={hasHoursPermission}
        triggerPhoneHoursEdit={triggerPhoneHoursEdit}
        onNavigateToCallRoutes={() => settingsNavigate({ to: '/phone/call-routes' })}
        onSaveCallRoute={handleSaveChangesAsync}
        callRouteIsSaving={saveCallRouteIsLoading}
        overrides={overridesData}
      />
    </SettingsPageLayout>
  );
};
